import { useCallback, useEffect, useRef } from 'react';

/**
 * Size an element to fill the remaining viewport height, turning it into
 * a bounded scroll box. Used by the matrix grids so their header row
 * (sticky-top) and label columns (sticky-left) pin within the box rather
 * than scrolling away with the page.
 *
 * Sets `height = innerHeight - element.top - bottomGap`, recomputed on
 * resize, once after first paint, and whenever the element (re)mounts —
 * the latter matters because the scroll box often only appears after its
 * data loads. Returns a callback ref to attach to the scroll container.
 */
export function useFillViewportHeight<T extends HTMLElement = HTMLDivElement>(
  bottomGap = 16,
) {
  const elRef = useRef<T | null>(null);

  const apply = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    el.style.height = `${Math.max(220, window.innerHeight - top - bottomGap)}px`;
  }, [bottomGap]);

  const setRef = useCallback(
    (node: T | null) => {
      elRef.current = node;
      if (node) {
        // Apply now, and again next frame once layout above settles.
        apply();
        requestAnimationFrame(apply);
      }
    },
    [apply],
  );

  useEffect(() => {
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [apply]);

  return setRef;
}
